import React, { useEffect, useMemo, useState } from "react";
import { useGetOrganizationsQuery } from "apis/organization";
import { useProductAnnouncementAudienceAccountsQuery } from "apis/productAnnouncement";
import SearchSelect from "components/ui/SearchSelect";
import {
  PRODUCT_ANNOUNCEMENT_ACCOUNT_TYPES,
  PRODUCT_ANNOUNCEMENT_AUDIENCE_SCOPES,
  PRODUCT_ANNOUNCEMENT_TARGETS,
  describeAnnouncementAudience,
} from "utils/productAnnouncement";

const AnnouncementAudience = ({ audience, accountDetails, onChange }) => {
  const { data: organizations = [] } = useGetOrganizationsQuery();
  const [filterOrganizationId, setFilterOrganizationId] = useState("");
  const [accountModel, setAccountModel] = useState("User");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedAccountDetails, setSelectedAccountDetails] = useState(
    accountDetails || [],
  );

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSelectedAccountDetails(accountDetails || []);
  }, [accountDetails]);

  const organizationOptions = organizations.map((organization) => ({
    value: organization._id,
    label: organization.name,
  }));
  const selectedOrganizations = organizationOptions.filter((option) =>
    audience.organizationIds.includes(option.value),
  );
  const permittedAccountTypes = useMemo(
    () =>
      PRODUCT_ANNOUNCEMENT_ACCOUNT_TYPES.filter((type) =>
        type.targetValues.some((target) => audience.targets.includes(target)),
      ),
    [audience.targets],
  );
  const selectedAccountModel = permittedAccountTypes.some(
    (type) => type.value === accountModel,
  )
    ? accountModel
    : permittedAccountTypes[0]?.value || "";
  const needsOrganization = selectedAccountModel === "User";
  const canSearch = needsOrganization
    ? Boolean(filterOrganizationId)
    : Boolean(filterOrganizationId || search);
  const audienceAccountsQuery = useProductAnnouncementAudienceAccountsQuery({
    organizationId: filterOrganizationId,
    accountModel: selectedAccountModel,
    search,
    enabled:
      audience.scope === "accounts" &&
      Boolean(selectedAccountModel) &&
      canSearch,
  });

  const setAudience = (changes) => onChange({ ...audience, ...changes });

  const toggleTarget = (target) => {
    const targets = audience.targets.includes(target)
      ? audience.targets.filter((value) => value !== target)
      : [...audience.targets, target];
    const permittedModels = PRODUCT_ANNOUNCEMENT_ACCOUNT_TYPES.filter((type) =>
      type.targetValues.some((value) => targets.includes(value)),
    ).map((type) => type.value);
    setAudience({
      targets,
      accounts: audience.accounts.filter((account) =>
        permittedModels.includes(account.accountModel),
      ),
    });
  };

  const selectScope = (scope) => {
    setAudience({
      scope,
      organizationIds: scope === "organizations" ? audience.organizationIds : [],
      accounts: scope === "accounts" ? audience.accounts : [],
    });
  };

  const addAccount = (account) => {
    if (audience.accounts.length >= 100) return;
    if (
      audience.accounts.some(
        (selected) =>
          selected.accountModel === account.accountModel &&
          selected.accountId === account.accountId,
      )
    ) {
      return;
    }
    setAudience({
      accounts: [
        ...audience.accounts,
        { accountModel: account.accountModel, accountId: account.accountId },
      ],
    });
    setSelectedAccountDetails((current) => [
      ...current.filter(
        (value) =>
          value.accountModel !== account.accountModel ||
          value.accountId !== account.accountId,
      ),
      account,
    ]);
  };

  const removeAccount = (account) => {
    setAudience({
      accounts: audience.accounts.filter(
        (selected) =>
          selected.accountModel !== account.accountModel ||
          selected.accountId !== account.accountId,
      ),
    });
  };

  const accountsWithoutApp = selectedAccountDetails.filter(
    (account) =>
      account.accountModel !== "User" &&
      !account.hasApp &&
      audience.accounts.some(
        (selected) =>
          selected.accountModel === account.accountModel &&
          selected.accountId === account.accountId,
      ),
  );

  const detailByAccount = new Map(
    selectedAccountDetails.map((account) => [
      `${account.accountModel}:${account.accountId}`,
      account,
    ]),
  );

  return (
    <section className="rounded-lg border border-mediumgray bg-white p-4">
      <h3 className="text-base font-semibold text-darkergray">Audience</h3>
      <p className="mt-1 text-xs text-darkgray">
        First choose who (agents, vendors, buyers), then whether it goes to all
        of them, only those in certain organizations, or specific people.
      </p>

      <div className="mt-4 space-y-5">
        <div>
          <div className="mb-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() =>
                setAudience({ targets: ["mobileUser", "mobileVendor", "mobileBuyer"] })
              }
              className="rounded-md border border-mediumgray px-3 py-1.5 text-xs text-darkergray"
            >
              All app users
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {PRODUCT_ANNOUNCEMENT_TARGETS.map((option) => (
              <label
                key={option.value}
                className={`rounded-lg border p-3 ${
                  option.disabled && !audience.targets.includes(option.value)
                    ? "cursor-not-allowed opacity-60"
                    : "cursor-pointer"
                } ${
                  audience.targets.includes(option.value)
                    ? "border-amber-500 bg-amber-50"
                    : "border-mediumgray"
                }`}
              >
                <div className="flex gap-2">
                  <input
                    type="checkbox"
                    checked={audience.targets.includes(option.value)}
                    onChange={() => toggleTarget(option.value)}
                    disabled={
                      option.disabled && !audience.targets.includes(option.value)
                    }
                  />
                  <div>
                    <div className="text-sm font-medium text-darkergray">
                      {option.label}
                    </div>
                    <div className="mt-1 text-xs text-darkgray">
                      {option.description}
                    </div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          {PRODUCT_ANNOUNCEMENT_AUDIENCE_SCOPES.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => selectScope(option.value)}
              className={`rounded-lg border p-3 text-left ${
                audience.scope === option.value
                  ? "border-amber-500 bg-amber-50"
                  : "border-mediumgray"
              }`}
            >
              <span className="block text-sm font-medium text-darkergray">
                {option.label}
              </span>
              <span className="mt-1 block text-xs text-darkgray">
                {option.description}
              </span>
            </button>
          ))}
        </div>

        <div className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-900">
          <span className="font-medium">Goes to: </span>
          {describeAnnouncementAudience(
            audience,
            selectedOrganizations.map((option) => option.label),
          )}
        </div>

        {audience.scope === "organizations" && (
          <div>
            <label className="mb-1 block text-sm font-medium text-darkergray">
              Organizations
            </label>
            <SearchSelect
              options={organizationOptions}
              value={selectedOrganizations}
              onChange={(options) =>
                setAudience({
                  organizationIds: options
                    .slice(0, 25)
                    .map((option) => option.value),
                })
              }
              placeholder="Search and select organizations"
              multiple
            />
            <p className="mt-1 text-xs text-darkgray">
              Up to 25 organizations.
            </p>
          </div>
        )}

        {audience.scope === "accounts" && (
          <div className="space-y-4 rounded-lg bg-lightgray p-4">
            {!audience.targets.length ? (
              <p className="text-sm text-darkgray">
                Select at least one App or Web audience before choosing people.
              </p>
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-darkergray">
                      1. Account type
                    </label>
                    <select
                      className="w-full rounded border border-mediumgray bg-white px-3 py-3 text-sm"
                      value={selectedAccountModel}
                      onChange={(event) => setAccountModel(event.target.value)}
                    >
                      {permittedAccountTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-darkergray">
                      2. Organization {needsOrganization ? "(required for agents)" : "(optional)"}
                    </label>
                    <SearchSelect
                      options={organizationOptions}
                      value={
                        organizationOptions.find(
                          (option) => option.value === filterOrganizationId,
                        ) || null
                      }
                      onChange={(option) =>
                        setFilterOrganizationId(option?.value || "")
                      }
                      placeholder={needsOrganization ? "Select organization" : "Any organization"}
                      isClearable
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-darkergray">
                      3. Search people
                    </label>
                    <input
                      className="w-full rounded border border-mediumgray bg-white px-3 py-3 text-sm"
                      value={searchInput}
                      onChange={(event) => setSearchInput(event.target.value)}
                      placeholder="Name, email, or phone"
                      disabled={needsOrganization && !filterOrganizationId}
                    />
                  </div>
                </div>

                {!canSearch && (
                  <p className="text-xs text-darkgray">
                    {needsOrganization
                      ? "Agents are listed by organization — pick the organization first."
                      : "Type a name, email, or phone to search every organization, or pick one organization to browse it."}
                  </p>
                )}

                {canSearch && (
                  <div className="max-h-56 space-y-2 overflow-y-auto">
                    {audienceAccountsQuery.isFetching && (
                      <p className="text-xs text-darkgray">Searching accounts…</p>
                    )}
                    {audienceAccountsQuery.isError && (
                      <p className="text-sm text-red-700">
                        Could not load accounts for this organization.
                      </p>
                    )}
                    {(audienceAccountsQuery.data || []).map((account) => {
                      const selected = audience.accounts.some(
                        (value) =>
                          value.accountModel === account.accountModel &&
                          value.accountId === account.accountId,
                      );
                      return (
                        <button
                          key={`${account.accountModel}:${account.accountId}`}
                          type="button"
                          onClick={() =>
                            selected ? removeAccount(account) : addAccount(account)
                          }
                          disabled={!selected && audience.accounts.length >= 100}
                          className="flex w-full items-center justify-between rounded border border-mediumgray bg-white px-3 py-2 text-left disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <span>
                            <span className="block text-sm font-medium text-darkergray">
                              {account.name}
                            </span>
                            <span className="block text-xs text-darkgray">
                              {account.email || account.phone || "No contact detail"}
                              {!account.hasApp && account.accountModel !== "User"
                                ? " · App account not activated"
                                : ""}
                            </span>
                          </span>
                          <span className="text-xs font-medium text-blue-700">
                            {selected ? "Remove" : "Add"}
                          </span>
                        </button>
                      );
                    })}
                    {!audienceAccountsQuery.isFetching &&
                      audienceAccountsQuery.data &&
                      !audienceAccountsQuery.data.length && (
                        <p className="text-sm text-darkgray">
                          No matching accounts.
                        </p>
                      )}
                  </div>
                )}

                {!!audience.accounts.length && (
                  <div>
                    <div className="mb-2 text-sm font-medium text-darkergray">
                      Selected people ({audience.accounts.length}/100)
                    </div>
                    {!!accountsWithoutApp.length && (
                      <p className="mb-2 text-xs text-amber-900">
                        {accountsWithoutApp.length} of them{" "}
                        {accountsWithoutApp.length === 1 ? "hasn't" : "haven't"} activated
                        the app yet, so they won&apos;t see this until they do.
                      </p>
                    )}
                    <div className="space-y-2">
                      {audience.accounts.map((account) => {
                        const detail = detailByAccount.get(
                          `${account.accountModel}:${account.accountId}`,
                        );
                        return (
                          <div
                            key={`${account.accountModel}:${account.accountId}`}
                            className="flex items-center justify-between rounded bg-white px-3 py-2 text-sm"
                          >
                            <span className="text-darkergray">
                              {detail?.name || detail?.email || account.accountId}
                              {detail &&
                                account.accountModel !== "User" &&
                                !detail.hasApp && (
                                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900">
                                    No app yet
                                  </span>
                                )}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeAccount(account)}
                              className="text-xs text-red-700"
                            >
                              Remove
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default AnnouncementAudience;
